import { supabase } from './config';
import { callEdgeFunction } from './edgeClient';
import { createLatestFetcher } from './realtimeFetch';
import { barcodesMatch } from '../utils/barcode';

// ============================================
// DATA TRANSFORMERS
// Convert Supabase format to match what components expect (timestamp object format)
// ============================================

function transformOrder(order) {
  return {
    id: order.order_id,
    orderId: order.order_id,
    customerName: order.customer_name,
    notes: order.notes,
    status: order.status,
    dispatchDate: order.dispatch_date ? { seconds: new Date(order.dispatch_date).getTime() / 1000 } : null,
    createTime: order.create_time ? { seconds: new Date(order.create_time).getTime() / 1000 } : null,
    updatedTime: order.updated_time ? { seconds: new Date(order.updated_time).getTime() / 1000 } : null,
    pickStartTime: order.pick_start_time ? { seconds: new Date(order.pick_start_time).getTime() / 1000 } : null,
    pickCompleteTime: order.pick_complete_time ? { seconds: new Date(order.pick_complete_time).getTime() / 1000 } : null,
    pickedCount: order.picked_count,
    totalItems: order.total_items,
    priorityWebhookSent: order.priority_webhook_sent,
    priorityWebhookResponse: order.priority_webhook_response,
    originalPriorityData: order.original_priority_data
  };
}

function transformItem(item) {
  return {
    id: item.item_id,
    itemId: item.item_id,
    originalItemId: item.original_item_id,
    name: item.name,
    barcode: item.barcode,
    quantity: item.quantity,
    kline: item.kline,
    warehouseBalance: item.warehouse_balance,
    picked: item.picked,
    pickedTime: item.picked_time ? { seconds: new Date(item.picked_time).getTime() / 1000 } : null,
    scanCount: item.scan_count
  };
}

// ============================================
// REALTIME SUBSCRIPTIONS
// ============================================

// Fetch once now, then refetch (debounced, latest-wins) on every realtime change.
const subscribeToQuery = (channelName, changeFilter, query, transform, callback, errorLabel) => {
  const fetcher = createLatestFetcher(query, ({ data, error }) => {
    if (error) {
      console.error(errorLabel, error);
      callback(null, error);
      return;
    }
    callback(transform(data), null);
  });

  fetcher.run();

  const channel = supabase
    .channel(channelName)
    .on('postgres_changes', { event: '*', schema: 'dispatch', ...changeFilter }, () => fetcher.schedule())
    .subscribe();

  // Return unsubscribe function
  return () => {
    fetcher.close();
    supabase.removeChannel(channel);
  };
};

// Get all "Ready to Pick" and "In Progress" orders
export const getReadyToPickOrders = (callback) => subscribeToQuery(
  'orders-ready-to-pick',
  { table: 'orders' },
  () => supabase
    .from('orders')
    .select('*')
    .in('status', ['Ready to Pick', 'In Progress'])
    .order('dispatch_date', { ascending: true }),
  (data) => data.map(transformOrder),
  callback,
  'Error fetching orders:'
);

// One-off fetch of the ready/in-progress orders (e.g. to pick the next order from a
// detail view without holding a realtime subscription)
export const fetchReadyToPickOrders = async () => {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .in('status', ['Ready to Pick', 'In Progress'])
    .order('dispatch_date', { ascending: true });
  if (error) throw error;
  return data.map(transformOrder);
};

// Get all "Picked Complete" orders
export const getCompletedOrders = (callback) => subscribeToQuery(
  'orders-completed',
  { table: 'orders' },
  () => supabase
    .from('orders')
    .select('*')
    .eq('status', 'Picked Complete')
    .order('pick_complete_time', { ascending: false }),
  (data) => data.map(transformOrder),
  callback,
  'Error fetching completed orders:'
);

// Subscribe to a single order so detail views reflect live status/timestamps
export const subscribeToOrder = (orderId, callback) => subscribeToQuery(
  `order-${orderId}`,
  { table: 'orders', filter: `order_id=eq.${orderId}` },
  () => supabase
    .from('orders')
    .select('*')
    .eq('order_id', orderId)
    .maybeSingle(),
  (data) => (data ? transformOrder(data) : null),
  callback,
  'Error fetching order:'
);

// Get items for a specific order
export const getOrderItems = (orderId, callback) => subscribeToQuery(
  `items-${orderId}`,
  { table: 'order_items', filter: `order_id=eq.${orderId}` },
  () => supabase
    .from('order_items')
    .select('*')
    .eq('order_id', orderId)
    .order('kline', { ascending: true }),
  (data) => data.map(transformItem),
  callback,
  'Error fetching order items:'
);

// ============================================
// ORDER OPERATIONS
// ============================================

// Delete an order and all its items
export const deleteOrder = async (orderId) => {
  try {
    // Items are deleted automatically via CASCADE
    const { error } = await supabase
      .from('orders')
      .delete()
      .eq('order_id', orderId);

    if (error) throw error;

    return {
      success: true,
      message: `Order ${orderId} deleted successfully`
    };
  } catch (error) {
    console.error('Error deleting order:', error);
    throw error;
  }
};

// Update item pick status
export const updateItemPickStatus = async (orderId, itemId, barcode) => {
  try {
    // Fetch the item
    const { data: item, error: fetchError } = await supabase
      .from('order_items')
      .select('*')
      .eq('order_id', orderId)
      .eq('item_id', itemId)
      .single();

    if (fetchError || !item) {
      throw new Error('Item not found');
    }

    // Handle UPC-A / EAN-13 leading-zero variations
    const storedBarcode = item.barcode || '';

    if (!barcodesMatch(barcode, storedBarcode)) {
      throw new Error(`Barcode does not match. Expected ${storedBarcode}, got ${barcode}`);
    }

    // Check if already picked
    if (item.picked) {
      throw new Error('Item already picked');
    }

    // Calculate new scan count and pick status
    const requiredQuantity = item.quantity || 1;
    const currentScanCount = item.scan_count || 0;
    const newScanCount = currentScanCount + 1;
    const isFullyPicked = newScanCount >= requiredQuantity;

    // Atomic conditional update: only update if scan_count hasn't changed since we read it.
    // If a concurrent scan already bumped it, affectedRows is 0 and we throw — preventing double-counts.
    const { data: updatedRows, error: itemUpdateError } = await supabase
      .from('order_items')
      .update({
        scan_count: newScanCount,
        picked: isFullyPicked,
        picked_time: isFullyPicked ? new Date().toISOString() : null
      })
      .eq('order_id', orderId)
      .eq('item_id', itemId)
      .eq('scan_count', currentScanCount)
      .eq('picked', false)
      .select();

    if (itemUpdateError) throw itemUpdateError;

    if (!updatedRows || updatedRows.length === 0) {
      throw new Error('Scan ignored — item was updated by another scan. Please re-scan if needed.');
    }

    // Fetch the order
    const { data: order, error: orderFetchError } = await supabase
      .from('orders')
      .select('*')
      .eq('order_id', orderId)
      .single();

    if (orderFetchError || !order) {
      throw new Error('Order not found');
    }

    // If this is the first scan, update status to "In Progress"
    if (order.status === 'Ready to Pick') {
      const { error: startError } = await supabase
        .from('orders')
        .update({
          status: 'In Progress',
          pick_start_time: new Date().toISOString()
        })
        .eq('order_id', orderId);

      if (startError) {
        throw new Error(`Scan recorded, but the order could not be marked In Progress (${startError.message}). Please refresh.`);
      }
    }

    // If item is fully picked, update picked count.
    // Recompute picked_count from the source of truth (a fresh count of picked items)
    // instead of incrementing a possibly-stale snapshot. Incrementing order.picked_count
    // lost updates when two pickers worked the same order concurrently, leaving fully
    // picked orders stuck "In Progress" forever.
    if (isFullyPicked) {
      const { count: freshPickedCount, error: countError } = await supabase
        .from('order_items')
        .select('item_id', { count: 'exact', head: true })
        .eq('order_id', orderId)
        .eq('picked', true);

      if (countError) throw countError;

      const newPickedCount = freshPickedCount || 0;

      // The order stays In Progress even when every line is picked: the operator has to
      // confirm the pick (confirmOrderPick) before it moves to Completed Orders.
      const { error: orderUpdateError } = await supabase
        .from('orders')
        .update({ picked_count: newPickedCount })
        .eq('order_id', orderId);

      if (orderUpdateError) {
        throw new Error(`Scan recorded, but the order progress could not be updated (${orderUpdateError.message}). Please refresh.`);
      }
    }

    return {
      success: true,
      message: isFullyPicked
        ? 'Item fully picked'
        : `Scan ${newScanCount} of ${requiredQuantity} completed`,
      isFullyPicked,
      scanCount: newScanCount,
      requiredQuantity
    };
  } catch (error) {
    console.error('Error updating item:', error);
    throw error;
  }
};

// Operator confirmation after every line is picked: moves the order to "Picked Complete".
// Re-checks the items server-side so a stale screen can't complete a partly picked order.
export const confirmOrderPick = async (orderId) => {
  const [{ count: totalCount, error: totalError }, { count: pickedCount, error: pickedError }] = await Promise.all([
    supabase
      .from('order_items')
      .select('item_id', { count: 'exact', head: true })
      .eq('order_id', orderId),
    supabase
      .from('order_items')
      .select('item_id', { count: 'exact', head: true })
      .eq('order_id', orderId)
      .eq('picked', true)
  ]);

  if (totalError) throw totalError;
  if (pickedError) throw pickedError;

  if (!totalCount || pickedCount < totalCount) {
    throw new Error(`Only ${pickedCount || 0} of ${totalCount || 0} items are picked. Finish picking before confirming.`);
  }

  // Conditional on status so a double tap or a second device can't overwrite the completion time
  const { data: updatedRows, error } = await supabase
    .from('orders')
    .update({
      status: 'Picked Complete',
      picked_count: pickedCount,
      pick_complete_time: new Date().toISOString()
    })
    .eq('order_id', orderId)
    .neq('status', 'Picked Complete')
    .select('order_id');

  if (error) throw error;

  return { success: true, alreadyConfirmed: !updatedRows || updatedRows.length === 0 };
};

// Refresh warehouse balance for all orders by re-triggering the webhook
export const refreshAllOrderBalances = async (orderIds) => {
  const results = await Promise.all(
    orderIds.map(async (orderId) => {
      try {
        const data = await callEdgeFunction('priority-order-webhook', {
          body: { ORDERS: { ORDNAME: orderId }, refresh: true }
        });

        return {
          orderId,
          success: Boolean(data?.success),
          itemCount: data?.itemCount || 0
        };
      } catch (error) {
        console.error(`Error refreshing order ${orderId}:`, error);
        return { orderId, success: false, itemCount: 0 };
      }
    })
  );

  const updated = results.filter(r => r.success).length;
  const failed = results.filter(r => !r.success).length;
  const totalItems = results.reduce((sum, r) => sum + r.itemCount, 0);

  return {
    success: failed === 0,
    updated,
    failed,
    totalItems,
    results
  };
};

// Delivery address for the box labels, read live from Priority (dispatch.orders stores none).
// Throws with a readable message; the caller shows it in the label dialog.
export const fetchShipTo = async (orderId) => {
  const data = await callEdgeFunction('fetch-ship-to', { body: { orderId } });
  if (!data?.success || !data.address) throw new Error(data?.error || 'No address returned');
  return { address: data.address, reference: data.reference || '', source: data.source };
};

// Note: sendOrderCompletionWebhook and testWebhook are removed
// Order completion sync to Priority will be added as a future feature
